import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store/rootReducer';

import { DatePicker, Select } from 'antd';
import Button from '../../Button';
import moment from 'moment';
import useAxios from '../../../hooks/useAxios';
import { saveRetireTodo } from '../../../api/campusClasses.api';
import Loader from '../../Loader';
import ErrorResponse from '../../ErrorResponse';
import { useSnackbar } from 'notistack';
import { infoVariant } from '../../../lib/barVariant';
import _ from 'lodash';
import Checkbox from '../../Checkbox';

const { Option } = Select;

interface IProps {
  setTab: (tab: string) => void;
}

const ClassRetire: FC<IProps> = ({
  setTab,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { data: res, isLoading, error, execute } = useAxios<any>();
  const { chosenClass } = useSelector((state: RootState) => state.class);
  const { timetables } = useSelector((state: RootState) => state.classTimetable);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // CL0004 = 퇴원 사유
  const CL0004 = commonCodes.find((item) => item.commonCode === 'CL0004')?.detailCode;

  // 세션 스토리지로 넘겨받는 값
  const [memberKorName, setMemberKorName] = useState<string | null>('');
  const [memberEngName, setMemberEngName] = useState<string | null>('');
  const [classStudentCode, setClassStudentCode] = useState<string | null>('');

  // input 값
  const [changeDate, setChangeDate] = useState('');
  const [retierRsnType, setRetierRsnType] = useState('');
  const [memo, setMemo] = useState('');

  const [isNoCount, setIsNoCount] = useState(false);
  const [dates, setDates] = useState<string[]>([]);

  useEffect(() => {
    setMemberKorName(sessionStorage.getItem('memberKorName'));
    setMemberEngName(sessionStorage.getItem('memberEngName'));
    setClassStudentCode(sessionStorage.getItem('classStudentCode'));
    sessionStorage.removeItem('memberKorName');
    sessionStorage.removeItem('memberEngName');
    sessionStorage.removeItem('classStudentCode');
  }, []);

  // 퇴원처리 성공 시 컴포넌트 이동
  useEffect(() => {
    if (res && !isLoading) {
      sessionStorage.setItem('calculateCode', res.data.calculateCode);
      setTab('retireResult');
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (timetables) {
      let _dates = timetables.map((item) => item.srtDate);
      _dates = _.sortBy(_.uniq(_dates));
      setDates(_dates);
    }
  }, [timetables]);

  const onSetChangeDate = (e: any) => {
    setChangeDate(moment(e).format('YYYYMMDD'));
  };

  const getLessonCount = () => {
    let count = 0;
    dates.forEach(date => {
      if (moment(date).isSameOrBefore(changeDate)) {
        count += 1;
      }
    });

    return isNoCount ? 0 : (count || '-');
  }

  const onSubmit = async () => {
    if (!classStudentCode) return;
    if (!changeDate && !isNoCount) {
      return enqueueSnackbar(t('class.class.message19'), infoVariant);
    }
    if (!retierRsnType) {
      return enqueueSnackbar(t('class.class.message20'), infoVariant);
    }

    const req = {
      changeDate: isNoCount
        ? moment(dates[0]).subtract(1, 'days').format('YYYYMMDD')
        : changeDate,
      retierRsnType,
      memo,
    };
    await execute(saveRetireTodo(classStudentCode, req));
  };
  
  return (
    <>
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <div className="comm-bx__between">        
        <h3 className='sub-title'>{chosenClass?.className}</h3>
      </div>

      <div className='comm-table__area rowType edit'>
        <table>
          <colgroup>
            <col width="20%" />
            <col width="*" />
          </colgroup>
          <tbody>
            <tr>
              <th className="text-center">{t('class.class.column47')}</th>
              <td>
                {memberKorName} ({memberEngName})
              </td>
            </tr>
            <tr>
              <th className="text-center align-middle">{`${t('class.class.column48')} *`}</th>
              <td>
                <div className='comm-gap'>
                  <div className="flex date-picker__area">
                    <DatePicker
                      value={changeDate ? moment(changeDate) : undefined}
                      onChange={(e) => {
                        if (e === null) setChangeDate('');
                        else onSetChangeDate(e);
                      }}
                      disabled={isNoCount}
                      disabledDate={current => !dates.includes(moment(current).format('YYYYMMDD'))}
                    />
                  </div>
                  <div>
                      ({t('class.class.text15')}: {getLessonCount()})
                    </div>
                </div>
                <div className="mt-2">
                  <Checkbox
                    checked={isNoCount}
                    onChange={() => setIsNoCount(!isNoCount)}
                    text={t('class.class.option2')}
                  />
                </div>
              </td>
            </tr>
            <tr>
              <th className="text-center align-middle">{`${t('class.class.column49')} *`}</th>
              <td>
                <Select
                  className="w-150p"
                  placeholder={t('common.button.select')}
                  onChange={(value) => setRetierRsnType(value)}
                >
                  {CL0004?.map((item: any) => {
                    return (
                      <Option key={item.detailCode} value={item.detailCode}>
                        {item.korName}
                      </Option>
                    );
                  })}
                </Select>
              </td>
            </tr>
            <tr>
              <th className="text-center align-middle">{t('class.class.column50')}</th>
              <td>
                <textarea
                  rows={3}
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink"
            onClick={onSubmit}
            text={t('common.button.confirm')}
          />
          <Button
            className="btn-basic gray"
            onClick={() => setTab('register')}
            text={t('common.button.cancel')}
          />
        </div>
      </div>
    </>
  );
};

export default ClassRetire;
